import { useEffect, useState } from 'react'
import { ExternalLink, KeyRound, Loader2 } from 'lucide-react'

import Modal from '@/components/common/Modal'
import {
  connectIOFlowApp,
  getConnectFields,
  getOpenInIOFlowUrl,
  listConnectableApps,
  type ConnectField,
  type ConnectableApp,
} from '@/api/integration'

interface Props {
  project: string
  open: boolean
  /** The app the agent named, when it knew which one it needed. */
  appId?: string
  onClose: () => void
  /** Called with the sentence to send back to the agent — an id, never a secret. */
  onConnected: (agentReply: string, subscriptionId: string) => void
}

/**
 * Connect an IOFlow application so a flow can actually call it.
 *
 * The whole point of this component is that the credential never touches the
 * agent: the user types it here, it goes straight to the backend and on to
 * IOFlow (which encrypts it), and what comes back — and what the agent is
 * told — is a subscription id. The same shape as the database
 * ConnectionDefineFlow, for the same reason.
 *
 * Fields are asked for from the server rather than hardcoded per app: they are
 * derived from the application's own auth_type/auth_config in IOFlow, so a
 * connector added to IOFlow tomorrow is connectable without a release here.
 *
 * OAuth2 is deliberately NOT offered as a form. It cannot work as one — it
 * needs a browser round-trip to the provider — so this says so and hands the
 * user the deep link instead of showing boxes that could never succeed.
 */
export default function IOFlowConnectForm({
  project,
  open,
  appId,
  onClose,
  onConnected,
}: Props) {
  const [apps, setApps] = useState<ConnectableApp[]>([])
  const [selected, setSelected] = useState(appId || '')
  const [fields, setFields] = useState<ConnectField[]>([])
  const [authType, setAuthType] = useState('')
  const [needsBrowser, setNeedsBrowser] = useState(false)
  const [values, setValues] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setError('')
    if (appId) setSelected(appId)
    if (apps.length > 0) return
    setLoading(true)
    listConnectableApps(project)
      .then(setApps)
      .catch((e) => setError(e?.response?.data?.detail || String(e)))
      .finally(() => setLoading(false))
    // apps is intentionally not a dependency: refetching on every render of an
    // open modal would flicker the picker while the user is choosing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appId, project])

  useEffect(() => {
    if (!open || !selected) return
    setLoading(true)
    setError('')
    getConnectFields(project, selected)
      .then((r) => {
        setFields(r.fields || [])
        setAuthType(r.auth_type || '')
        setNeedsBrowser(!!r.needs_browser)
        const seed: Record<string, string> = {}
        for (const f of r.fields || []) seed[f.name] = f.default || ''
        setValues(seed)
      })
      .catch((e) => setError(e?.response?.data?.detail || String(e)))
      .finally(() => setLoading(false))
  }, [open, selected, project])

  const openInIOFlow = async () => {
    try {
      const url = await getOpenInIOFlowUrl(project, 'application')
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      setError(String(e))
    }
  }

  const missing = fields.filter((f) => f.required && !(values[f.name] || '').trim())

  const submit = async () => {
    if (!selected || missing.length > 0) return
    setSaving(true)
    setError('')
    try {
      const res = await connectIOFlowApp(project, selected, values)
      // Clear immediately — no reason for a secret to sit in component state
      // any longer than the request it was needed for.
      setValues({})
      if (!res.ok) {
        setError('IOFlow did not return a subscription. Check the credentials.')
        return
      }
      onConnected(res.agent_reply, res.subscription_id)
    } catch (e: any) {
      setValues({})
      setError(e?.response?.data?.detail || String(e))
    } finally {
      setSaving(false)
    }
  }

  const app = apps.find((a) => a.app_id === selected)

  return (
    <Modal open={open} onClose={onClose} title="Connect an application">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <p style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--color-text-secondary)' }}>
          The flow needs to call this application. Your credentials go straight to
          IOFlow, which stores them encrypted — they are never shown to the agent
          or saved here.
        </p>

        <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>
          Application
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            style={{
              width: '100%',
              marginTop: '0.25rem',
              padding: '0.4rem 0.5rem',
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text)',
            }}
          >
            <option value="">Select an application…</option>
            {apps.map((a) => (
              <option key={a.app_id} value={a.app_id}>
                {a.app_name}
                {a.connected ? ' (connected)' : ''}
              </option>
            ))}
          </select>
        </label>

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.9375rem' }}>
            <Loader2 size={14} className="spin" /> Loading…
          </div>
        )}

        {selected && needsBrowser && (
          <div
            style={{
              padding: '0.625rem',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.9375rem',
            }}
          >
            <strong>{app?.app_name || 'This application'}</strong> uses OAuth
            sign-in, so it cannot be connected with a form — you have to approve
            access in IOFlow itself.
            <div style={{ marginTop: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={openInIOFlow}>
                <ExternalLink size={12} /> Authorize in IOFlow
              </button>
            </div>
          </div>
        )}

        {selected && !needsBrowser && authType === 'none' && (
          <p style={{ margin: 0, fontSize: '0.9375rem' }}>
            This application needs no credentials — connecting it is enough.
          </p>
        )}

        {selected &&
          !needsBrowser &&
          fields.map((f) => (
            <label key={f.name} style={{ fontSize: '0.875rem', fontWeight: 600 }}>
              {f.label}
              {f.required ? ' *' : ''}
              <input
                type={f.secret ? 'password' : 'text'}
                autoComplete={f.secret ? 'new-password' : 'off'}
                value={values[f.name] || ''}
                onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                style={{
                  width: '100%',
                  marginTop: '0.25rem',
                  padding: '0.4rem 0.5rem',
                  background: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text)',
                }}
              />
            </label>
          ))}

        {error && (
          <div style={{ fontSize: '0.9375rem', color: 'var(--color-error, #f44336)' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
          {!needsBrowser && (
            <button
              className="btn btn-primary btn-sm"
              disabled={!selected || saving || missing.length > 0}
              onClick={submit}
            >
              {saving ? <Loader2 size={12} className="spin" /> : <KeyRound size={12} />}
              Connect
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}
